import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, 
  ChevronDown, 
  ChevronUp, 
  Users, 
  AlertCircle, 
  CheckCircle, 
  Shield, 
  Undo, 
  Loader2, 
  Package,
  List,
  ChevronRight,
  Search,
  User,
  UserX,
  FileText,
  Download,
  PieChart
} from 'lucide-react';
import { useGuidesStore } from '../stores/guidesStore';
import { usePilgrimGuideStore } from '../stores/pilgrimGuideStore';
import { useOrganizersStore } from '../stores/organizersStore';
import { usePackagesStore } from '../stores/packagesStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { LoadingProgress } from '../components/common/LoadingProgress';
import { useLanguage } from '../contexts/LanguageContext';
import { Flag } from '../components/common/Flag';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';

type OrganizerStats = {
  totalPilgrims: number;
  approvedPilgrims: number;
  ownerPilgrims: number;
  notOwnerPilgrims: number;
  disputedPilgrims: number;
  guides: Array<{
    id: string;
    name: string;
    approvedCount: number;
    ownerCount: number;
    notOwnerCount: number;
    disputedCount: number;
    verified?: boolean;
    verifiedAt?: number;
    verifiedBy?: string;
    verifiedByName?: string;  // Added to store the name of the verifier
  }>;
  verifiedGuides: number;
  unverifiedGuides: number;
};

type PackageStats = {
  [packageName: string]: {
    count: number;
    pilgrims: Array<{
      id: string;
      name: string;
      passport: string;
      country: string;
    }>;
  };
};

type VerificationSummary = {
  totalGuides: number;
  verifiedGuides: number;
  unverifiedGuides: number;
  verificationPercentage: number;
};

export const OrganizersOverviewPage = () => {
  const { translations } = useLanguage();
  const [organizerStats, setOrganizerStats] = useState<Record<string, OrganizerStats>>({});
  const [expandedOrganizer, setExpandedOrganizer] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState<string | null>(null);
  const [isUnverifying, setIsUnverifying] = useState<string | null>(null);
  const [packageStats, setPackageStats] = useState<Record<string, PackageStats>>({});
  const [selectedOrganizer, setSelectedOrganizer] = useState<string | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [overallStats, setOverallStats] = useState<VerificationSummary>({
    totalGuides: 0,
    verifiedGuides: 0,
    unverifiedGuides: 0,
    verificationPercentage: 0
  });
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  
  const { 
    guides, 
    loading: guidesLoading, 
    initialized: guidesInitialized,
    initializeListener: initializeGuides,
    updateGuide
  } = useGuidesStore();
  
  const { 
    links, 
    loading: linksLoading, 
    initialized: linksInitialized,
    progress: linksProgress,
    fetchLinks 
  } = usePilgrimGuideStore();
  
  const { 
    organizers, 
    loading: organizersLoading, 
    initialized: organizersInitialized,
    fetchOrganizers 
  } = useOrganizersStore();

  const {
    packages,
    loading: packagesLoading,
    fetchPackages
  } = usePackagesStore();

  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName');

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      if (!guidesInitialized) {
        initializeGuides();
      }
      if (!linksInitialized) {
        await fetchLinks();
      }
      if (!organizersInitialized) {
        await fetchOrganizers();
      }
      await fetchPackages();
    };

    initializeData();
  }, [guidesInitialized, linksInitialized, organizersInitialized, initializeGuides, fetchLinks, fetchOrganizers, fetchPackages]);

  // Load user names for verifications
  useEffect(() => {
    const loadUserNames = async () => {
      const verifierIds = new Set<string>();
      
      // Collect all verifier IDs
      Object.values(guides).forEach(guide => {
        if (guide.verified && guide.verifiedBy) {
          verifierIds.add(guide.verifiedBy);
        }
      });
      
      if (verifierIds.size === 0) return;
      
      const nameMap: Record<string, string> = {};
      
      // Fetch user names
      for (const verifierId of verifierIds) {
        try {
          // Try to get the user from Users collection first
          let userName = null;
          const userDoc = await getDoc(doc(firestore, 'Users', verifierId));
          
          if (userDoc.exists()) {
            userName = userDoc.data().name;
          } else {
            // If not in Users, try Guides
            const guideDoc = await getDoc(doc(firestore, 'Guides', verifierId));
            if (guideDoc.exists()) {
              userName = guideDoc.data().name;
            } else {
              // If not in Guides, try Organizers
              const organizerDoc = await getDoc(doc(firestore, 'Organizers', verifierId));
              if (organizerDoc.exists()) {
                userName = organizerDoc.data().name;
              }
            }
          }
          
          nameMap[verifierId] = userName || 'Unknown Admin';
        } catch (error) {
          console.error(`Error fetching user name for ID ${verifierId}:`, error);
          nameMap[verifierId] = 'Unknown Admin';
        }
      }
      
      setUserNames(nameMap);
    };
    
    if (Object.keys(guides).length > 0) {
      loadUserNames();
    }
  }, [guides]);

  // Calculate stats
  useEffect(() => {
    const calculateStats = () => {
      const stats: Record<string, OrganizerStats> = {};
      const pkgStats: Record<string, PackageStats> = {};
      let totalGuides = 0;
      let totalVerifiedGuides = 0;

      // Filter by organizer if not admin
      const relevantOrganizers = userRole === 'admin' ? 
        Object.values(organizers) :
        Object.values(organizers).filter(org => org.id === userId);

      // Initialize stats for each organizer
      relevantOrganizers.forEach(organizer => {
        stats[organizer.id] = {
          totalPilgrims: 0,
          approvedPilgrims: 0,
          ownerPilgrims: 0,
          notOwnerPilgrims: 0,
          disputedPilgrims: 0,
          guides: [],
          verifiedGuides: 0,
          unverifiedGuides: 0
        };

        pkgStats[organizer.id] = {};
      });

      // Group guides by organizer
      Object.values(guides)
        .filter(guide => userRole === 'admin' || guide.organizerId === userId)
        .forEach(guide => {
          if (!stats[guide.organizerId]) return;
          
          totalGuides++;
          if (guide.verified) {
            totalVerifiedGuides++;
            stats[guide.organizerId].verifiedGuides++;
          } else {
            stats[guide.organizerId].unverifiedGuides++;
          }

          const guideStats = {
            id: guide.id,
            name: guide.name,
            approvedCount: 0,
            ownerCount: 0,
            notOwnerCount: 0,
            disputedCount: 0,
            verified: guide.verified,
            verifiedAt: guide.verifiedAt,
            verifiedBy: guide.verifiedBy,
            verifiedByName: guide.verifiedBy ? userNames[guide.verifiedBy] : undefined
          };

          // Count pilgrims for each guide and build package stats
          Object.values(links).forEach(link => {
            if (link.guideId === guide.id) {
              // Update guide count
              if (link.status === 'approved') {
                guideStats.approvedCount++;
                stats[guide.organizerId].approvedPilgrims++;
                
                // Count owner vs not-owner pilgrims
                if (link.isOwner === false) {
                  guideStats.notOwnerCount++;
                  stats[guide.organizerId].notOwnerPilgrims++;
                } else {
                  guideStats.ownerCount++;
                  stats[guide.organizerId].ownerPilgrims++;
                }
              } else if (link.status === 'disputed') {
                guideStats.disputedCount++;
                stats[guide.organizerId].disputedPilgrims++;
              }
              stats[guide.organizerId].totalPilgrims++;

              // Update package stats
              if (!pkgStats[guide.organizerId][link.packageName]) {
                pkgStats[guide.organizerId][link.packageName] = {
                  count: 0,
                  pilgrims: []
                };
              }

              pkgStats[guide.organizerId][link.packageName].count++;
              pkgStats[guide.organizerId][link.packageName].pilgrims.push({
                id: link.pilgrimId,
                name: link.pilgrimName,
                passport: link.passportNumber,
                country: link.residenceCountry
              });
            }
          });

          stats[guide.organizerId].guides.push(guideStats);
        });

      // Sort guides by total pilgrims
      Object.values(stats).forEach(stat => {
        stat.guides.sort((a, b) => 
          (b.approvedCount + b.disputedCount) - (a.approvedCount + a.disputedCount)
        );
      });

      // Calculate overall verification stats
      setOverallStats({
        totalGuides,
        verifiedGuides: totalVerifiedGuides,
        unverifiedGuides: totalGuides - totalVerifiedGuides,
        verificationPercentage: totalGuides > 0 ? Math.round((totalVerifiedGuides / totalGuides) * 100) : 0
      });

      setOrganizerStats(stats);
      setPackageStats(pkgStats);
      setLoading(false);
    };

    if (!guidesLoading && !linksLoading && !organizersLoading && !packagesLoading) {
      calculateStats();
    }
  }, [guides, links, organizers, packages, userRole, userId, guidesLoading, linksLoading, organizersLoading, packagesLoading, userNames]);

  const handleVerifyGuide = async (guideId: string) => {
    if (userRole !== 'admin') return;
    
    setIsVerifying(guideId);
    try {
      await updateGuide(guideId, {
        verified: true,
        verifiedAt: Date.now(),
        verifiedBy: userId
      });
      toast.success('Guide verified successfully');
    } catch (error) {
      console.error('Error verifying guide:', error);
      toast.error('Failed to verify guide');
    } finally {
      setIsVerifying(null);
    }
  };

  const handleUnverifyGuide = async (guideId: string) => {
    if (userRole !== 'admin') return;
    
    setIsUnverifying(guideId);
    try {
      await updateGuide(guideId, {
        verified: false,
        verifiedAt: null, // Changed from undefined to null
        verifiedBy: null  // Changed from undefined to null
      });
      toast.success('Guide verification removed');
    } catch (error) {
      console.error('Error removing guide verification:', error);
      toast.error('Failed to remove guide verification');
    } finally {
      setIsUnverifying(null);
    }
  };

  const handleViewPackages = (organizerId: string) => {
    setSelectedOrganizer(organizerId);
  };

  // Filter pilgrims for selected package based on search term
  const filteredPilgrims = React.useMemo(() => {
    if (!selectedOrganizer || !selectedPackage || !packageStats[selectedOrganizer]?.[selectedPackage]) {
      return [];
    }

    return packageStats[selectedOrganizer][selectedPackage].pilgrims.filter(pilgrim =>
      !searchTerm ||
      pilgrim.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pilgrim.passport.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pilgrim.country.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [selectedOrganizer, selectedPackage, packageStats, searchTerm]);

  const handleExport = () => {
  try {
    setIsExporting(true);

    const exportData = [];

    Object.entries(organizerStats).forEach(([organizerId, stats]) => {
      const organizerName = organizers[organizerId]?.name || 'Unknown';
      const organizerCountry = organizers[organizerId]?.country || '';

      stats.guides.forEach(guide => {
        exportData.push({
          'Organizer': organizerName,
          'Country': getCountryNameFromCode(organizerCountry),
          'Guide Name': guide.name,
          'Verified': guide.verified ? 'Yes' : 'No'
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(exportData);

    ws['!cols'] = [
      { wch: 25 }, // Organizer
      { wch: 15 }, // Country
      { wch: 25 }, // Guide Name
      { wch: 10 }  // Verified
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Organizers Verification');

    const date = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `organizers-guides-verification-${date}.xlsx`);

    toast.success('Export completed successfully');
  } catch (error) {
    console.error('Error exporting data:', error);
    toast.error('Failed to export data');
  } finally {
    setIsExporting(false);
  }
};


  if (loading || guidesLoading || linksLoading || organizersLoading) {
    return (
      <div className="space-y-6">
        {/* Show progress for links loading */}
        {linksLoading && !linksInitialized && linksProgress.total > 0 && (
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <LoadingProgress
              progress={linksProgress.current}
              total={linksProgress.total}
              label={translations?.loading?.loadingLinks || 'Loading links...'}
            />
          </div>
        )}
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Building2 className="text-primary-500" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {translations?.organizersOverview?.title || 'Organizers Overview'}
            </h1>
            <p className="text-gray-500 mt-1">
              {translations?.organizersOverview?.subtitle || 'View pilgrim distribution across organizers and guides'}
            </p>
          </div>
        </div>
        
        <button
          onClick={handleExport}
          disabled={isExporting}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
            isExporting 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
          } transition-colors`}
        >
          {isExporting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              <span>Exporting...</span>
            </>
          ) : (
            <>
              <FileText size={18} />
              <span>Export Verification Data</span>
            </>
          )}
        </button>
      </div>

      {/* Overall Verification Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-50 rounded-lg">
              <Users className="text-primary-500" size={24} />
            </div>
            <h3 className="font-medium text-lg">Total Guides</h3>
          </div>
          <div className="text-3xl font-bold text-primary-600">{overallStats.totalGuides}</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-50 rounded-lg">
              <Shield className="text-green-500" size={24} />
            </div>
            <h3 className="font-medium text-lg">Verified Guides</h3>
          </div>
          <div className="text-3xl font-bold text-green-600">{overallStats.verifiedGuides}</div>
          <div className="text-sm text-green-600 mt-1">
            {overallStats.verificationPercentage}% of total guides
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-50 rounded-lg">
              <UserX className="text-red-500" size={24} />
            </div>
            <h3 className="font-medium text-lg">Unverified Guides</h3>
          </div>
          <div className="text-3xl font-bold text-red-600">{overallStats.unverifiedGuides}</div>
          <div className="text-sm text-red-600 mt-1">
            {100 - overallStats.verificationPercentage}% of total guides
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-50 rounded-lg">
              <PieChart className="text-blue-500" size={24} />
            </div>
            <h3 className="font-medium text-lg">Verification Rate</h3>
          </div>

          {/* Progress Circle */}
          <div className="flex justify-center items-center">
            <div className="relative w-20 h-20">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                {/* Background Circle */}
                <circle
                  className="text-gray-200"
                  strokeWidth="10"
                  stroke="currentColor"
                  fill="transparent"
                  r="40"
                  cx="50"
                  cy="50"
                />
                {/* Progress Circle */}
                <circle
                  className="text-primary-500"
                  strokeWidth="10"
                  strokeLinecap="round"
                  stroke="currentColor"
                  fill="transparent"
                  r="40"
                  cx="50"
                  cy="50"
                  strokeDasharray={`${2 * Math.PI * 40}`}
                  strokeDashoffset={`${2 * Math.PI * 40 * (1 - overallStats.verificationPercentage / 100)}`}
                  transform="rotate(-90 50 50)"
                />
                <text
                  x="50"
                  y="50"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="font-bold text-lg"
                  fill="#00897B"
                >
                  {overallStats.verificationPercentage}%
                </text>
              </svg>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Package Details Modal */}
      <AnimatePresence>
        {selectedOrganizer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => {
              setSelectedOrganizer(null);
              setSelectedPackage(null);
              setSearchTerm('');
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-6 border-b bg-primary-50">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Package className="text-primary-500" size={24} />
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {organizers[selectedOrganizer]?.name}
                        {organizers[selectedOrganizer]?.country && (
                          <Flag 
                            countryCode={organizers[selectedOrganizer].country} 
                            size={20} 
                            className="ml-2 inline-block"
                          />
                        )}
                      </h2>
                      <p className="text-gray-600">
                        {selectedPackage 
                          ? `${translations?.organizersOverview?.pilgrimsInPackage || 'Pilgrims in package:'} ${selectedPackage}` 
                          : translations?.organizersOverview?.selectPackage || 'Select a package to view pilgrims'}
                      </p>
                    </div>
                  </div>
                  <button
                    className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                    onClick={() => {
                      setSelectedOrganizer(null);
                      setSelectedPackage(null);
                      setSearchTerm('');
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="flex h-[calc(90vh-100px)]">
                {/* Packages Sidebar */}
                <div className="w-64 border-r overflow-y-auto p-4 bg-gray-50 shrink-0">
                  <h3 className="font-medium mb-3 text-gray-700">Packages</h3>
                  <div className="space-y-2">
                    {Object.entries(packageStats[selectedOrganizer] || {})
                      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
                      .map(([packageName, stats]) => (
                        <motion.button
                          key={packageName}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setSelectedPackage(packageName)}
                          className={`w-full text-left p-3 rounded-lg flex items-center justify-between ${
                            selectedPackage === packageName 
                              ? 'bg-primary-100 text-primary-800'
                              : 'bg-white hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Package size={16} className={selectedPackage === packageName ? 'text-primary-500' : 'text-gray-500'} />
                            <span className="font-medium truncate max-w-[150px]">{packageName}</span>
                          </div>
                          <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs font-medium">
                            {stats.count}
                          </span>
                        </motion.button>
                    ))}
                  </div>
                </div>

                {/* Pilgrims Content */}
                <div className="flex-1 flex flex-col overflow-hidden">
                  {selectedPackage ? (
                    <>
                      {/* Search Bar */}
                      <div className="p-4 border-b">
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search pilgrims..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                          />
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        </div>
                      </div>

                      {/* Pilgrims List */}
                      <div className="flex-1 overflow-y-auto p-4">
                        {filteredPilgrims.length > 0 ? (
                          <div className="space-y-3">
                            {filteredPilgrims.map((pilgrim) => (
                              <motion.div
                                key={pilgrim.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow transition-shadow"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex gap-3">
                                    <div className="mt-1">
                                      <User className="text-primary-500" size={20} />
                                    </div>
                                    <div>
                                      <h4 className="font-medium text-gray-900">{pilgrim.name}</h4>
                                      <p className="text-sm text-gray-600">
                                        {translations?.pilgrimInfo?.passport || 'Passport'}: {pilgrim.passport}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Flag countryCode={getCountryCode(pilgrim.country)} size={20} />
                                    <span className="text-sm text-gray-600">{pilgrim.country}</span>
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-gray-400">
                            <Users size={48} className="mb-2" />
                            <p>
                              {searchTerm 
                                ? translations?.organizersOverview?.noMatchingPilgrims || 'No matching pilgrims found' 
                                : translations?.organizersOverview?.noPilgrims || 'No pilgrims in this package'}
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                      <Package size={48} className="mb-2" />
                      <p>{translations?.organizersOverview?.selectPackagePrompt || 'Select a package from the sidebar'}</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6">
        {Object.entries(organizerStats)
          .sort(([, a], [, b]) => b.totalPilgrims - a.totalPilgrims)
          .map(([organizerId, stats]) => {
            const organizer = organizers[organizerId];
            const isExpanded = expandedOrganizer === organizerId;

            if (!organizer) return null;

            return (
              <motion.div
                key={organizerId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                {/* Organizer Header */}
                <div 
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedOrganizer(isExpanded ? null : organizerId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-primary-50 rounded-lg">
                        <Building2 className="text-primary-500" size={24} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-xl font-semibold">{organizer.name}</h2>
                          {organizer.country && (
                            <Flag 
                              countryCode={organizer.country} 
                              size={24} 
                              className="inline-block ml-2"
                            />
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-4 mt-2">
                          <div className="flex items-center gap-2 text-gray-500">
                            <Users size={16} />
                            <span>
                              {stats.guides.length} guides ({stats.verifiedGuides} verified, {stats.unverifiedGuides} unverified)
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle size={16} className="text-green-500" />
                            <span>
                              {stats.approvedPilgrims} approved
                              {stats.notOwnerPilgrims > 0 && (
                                <span className="text-sm ml-1">
                                  ({stats.ownerPilgrims} owner, {stats.notOwnerPilgrims} <span className="text-orange-500 font-medium">not owner</span>)
                                </span>
                              )}
                            </span>
                          </div>
                          {stats.disputedPilgrims > 0 && (
                            <div className="flex items-center gap-2">
                              <AlertCircle size={16} className="text-red-500" />
                              <span>{stats.disputedPilgrims} disputed</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-2xl font-bold text-primary-600">
                        {stats.totalPilgrims}
                      </div>
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </div>
                </div>
                
                {/* View Packages Button */}
                <div className="px-6 py-3 border-t border-b bg-gray-50">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewPackages(organizerId);
                    }}
                    className="flex items-center gap-2 w-full text-left px-4 py-2 bg-white border rounded-lg hover:bg-primary-50 transition-colors text-primary-600 hover:text-primary-700 hover:border-primary-100"
                  >
                    <Package size={18} />
                    <span className="font-medium">
                      {translations?.organizersOverview?.viewPackages || 'View Packages and Pilgrims'}
                    </span>
                    <ChevronRight size={16} className="ml-auto" />
                  </button>
                </div>

                {/* Guides List */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t"
                    >
                      {/* Guide Verification Stats */}
                      <div className="p-4 border-b bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-white p-3 rounded-lg shadow-sm flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Users size={18} className="text-gray-500" />
                              <span className="font-medium">Total Guides:</span>
                            </div>
                            <span className="text-lg font-bold text-gray-700">{stats.guides.length}</span>
                          </div>
                          <div className="bg-white p-3 rounded-lg shadow-sm flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Shield size={18} className="text-green-500" />
                              <span className="font-medium">Verified:</span>
                            </div>
                            <span className="text-lg font-bold text-green-600">{stats.verifiedGuides}</span>
                          </div>
                          <div className="bg-white p-3 rounded-lg shadow-sm flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <UserX size={18} className="text-red-500" />
                              <span className="font-medium">Unverified:</span>
                            </div>
                            <span className="text-lg font-bold text-red-600">{stats.unverifiedGuides}</span>
                          </div>
                        </div>
                      </div>

                      <div className="divide-y">
                        {stats.guides.map((guide) => (
                          <div key={guide.id} className="p-4 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-primary-50 rounded-full">
                                  <Users className="text-primary-500" size={20} />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium truncate">{guide.name}</span>
                                    {guide.verified && (
                                      <div className="flex items-center gap-1">
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                                          <Shield size={12} />
                                          <span>Verified</span>
                                        </span>
                                        {guide.verifiedBy && (
                                          <span className="text-xs text-gray-500 ml-1">
                                            by {guide.verifiedByName || userNames[guide.verifiedBy] || 'Admin'}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap items-center gap-4 mt-1">
                                    <div className="flex items-center gap-2">
                                      <CheckCircle size={14} className="text-green-500" />
                                      <span className="text-sm">
                                        {guide.approvedCount} approved
                                        {guide.notOwnerCount > 0 && (
                                          <span className="text-xs ml-1">
                                            ({guide.ownerCount} owner, {guide.notOwnerCount} <span className="text-orange-500 font-medium">not owner</span>)
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                    {guide.disputedCount > 0 && (
                                      <div className="flex items-center gap-2">
                                        <AlertCircle size={14} className="text-red-500" />
                                        <span className="text-sm">{guide.disputedCount} disputed</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-xl font-bold text-primary-600">
                                  {guide.approvedCount + guide.disputedCount}
                                </div>
                                {userRole === 'admin' && (
                                  guide.verified ? (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUnverifyGuide(guide.id);
                                      }}
                                      disabled={isUnverifying === guide.id}
                                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                                    >
                                      {isUnverifying === guide.id ? (
                                        <Loader2 size={14} className="animate-spin" />
                                      ) : (
                                        <Undo size={14} />
                                      )}
                                      <span>Unverify</span>
                                    </button>
                                  ) : (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleVerifyGuide(guide.id);
                                      }}
                                      disabled={isVerifying === guide.id}
                                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                                    >
                                      {isVerifying === guide.id ? (
                                        <Loader2 size={14} className="animate-spin" />
                                      ) : (
                                        <Shield size={14} />
                                      )}
                                      <span>Verify</span>
                                    </button>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
      </div>
    </div>
  );
};

// Helper function to get country code from country name
const getCountryCode = (countryName: string): string => {
  // Map of common country names to ISO codes
  const countryMap: Record<string, string> = {
    'United Kingdom': 'GB',
    'UK': 'GB',
    'United States': 'US',
    'USA': 'US',
    'France': 'FR',
    'Germany': 'DE',
    'Italy': 'IT',
    'Spain': 'ES',
    'Netherlands': 'NL',
    'Belgium': 'BE',
    'Switzerland': 'CH',
    'Canada': 'CA',
    'China': 'CN',
    'Sweden': 'SE',
    'Saudi Arabia': 'SA',
    'Egypt': 'EG',
    'Turkey': 'TR',
    'Morocco': 'MA',
    'Algeria': 'DZ',
    'Tunisia': 'TN',
    'Jordan': 'JO',
    'Lebanon': 'LB',
    'United Arab Emirates': 'AE',
    'UAE': 'AE',
    'Qatar': 'QA',
    'Kuwait': 'KW',
    'Bahrain': 'BH',
    'Oman': 'OM',
    'Iraq': 'IQ',
    'Syria': 'SY',
    'Yemen': 'YE',
    'Palestine': 'PS',
    'Libya': 'LY',
    'Sudan': 'SD',
    'Somalia': 'SO',
    'Djibouti': 'DJ',
    'Comoros': 'KM',
    'Mauritania': 'MR',
    'Pakistan': 'PK',
    'India': 'IN',
    'Bangladesh': 'BD',
    'Indonesia': 'ID',
    'Malaysia': 'MY',
    'Singapore': 'SG',
    'Philippines': 'PH',
    'Thailand': 'TH',
    'Vietnam': 'VN',
    'Japan': 'JP',
    'South Korea': 'KR',
    'Australia': 'AU',
    'New Zealand': 'NZ',
    'South Africa': 'ZA',
    'Nigeria': 'NG',
    'Kenya': 'KE',
    'Ghana': 'GH',
    'Senegal': 'SN',
    'Brazil': 'BR',
    'Argentina': 'AR',
    'Mexico': 'MX',
    'Colombia': 'CO',
    'Chile': 'CL',
    'Peru': 'PE',
    'Russia': 'RU',
    'Ukraine': 'UA',
    'Poland': 'PL',
      'NZ': 'New Zealand'

  };
  
  return countryMap[countryName] || 'UN'; // Return UN as fallback for unknown countries
};

// Helper function to get country name from code
const getCountryNameFromCode = (code: string): string => {
  // Just the inverse of the country map above, for common countries
  const countryMap: Record<string, string> = {
    'GB': 'United Kingdom',
    'US': 'United States',
    'FR': 'France',
    'DE': 'Germany',
    'IT': 'Italy',
    'ES': 'Spain',
    'NL': 'Netherlands',
    'BE': 'Belgium',
    'CH': 'Switzerland',
    'CA': 'Canada',
    'CN': 'China',
    'SE': 'Sweden',
    'SA': 'Saudi Arabia'
  };
  
  return countryMap[code] || code; // Return the code itself if not found
};